import type {QueryOrQueryRequest} from '@rocicorp/zero'
import {useQuery, type UseQueryOptions} from '@rocicorp/zero/react'
import {useCallback} from 'react'
import {assert, unreachable} from '../asserts'

// oxlint-disable no-explicit-any

/**
 * Represents a position in the virtualized list used for pagination.
 *
 * @typeParam TStartRow - The type of data needed to anchor pagination
 */
export type Anchor<TStartRow> =
  | Readonly<{
      index: number
      kind: 'forward'
      startRow?: TStartRow | undefined
    }>
  | Readonly<{
      index: number
      kind: 'backward'
      startRow: TStartRow
    }>
  | Readonly<{
      index: number
      kind: 'permalink'
      id: string
    }>

/** Result returned by query functions: a query plus optional per-query options. */
export type QueryResult<TReturn> = {
  query: GetQueryReturnType<TReturn>
  options?: UseQueryOptions
}

/**
 * Options passed to {@link GetPageQuery}.
 *
 * @typeParam TStartRow - The type of data needed to anchor pagination
 */
export type GetPageQueryOptions<TStartRow> = {
  /** The maximum number of rows to return */
  limit: number
  /** The start row data to anchor the query, or null if starting from the beginning */
  start: TStartRow | null
  /** The direction to paginate ('forward' or 'backward') */
  dir: 'forward' | 'backward'
  /** Whether the list has been idle for `settleTime` ms */
  settled: boolean
}

/**
 * Function that returns a query for fetching a page of rows.
 *
 * @typeParam TRow - The type of row data returned from queries
 * @typeParam TStartRow - The type of data needed to anchor pagination
 */
export type GetPageQuery<TRow, TStartRow> = (options: GetPageQueryOptions<TStartRow>) => QueryResult<TRow>

/**
 * Options passed to {@link GetSingleQuery}.
 */
export type GetSingleQueryOptions = {
  /** The ID of the row to fetch */
  id: string
  /** Whether the list has been idle for `settleTime` ms */
  settled: boolean
}

/**
 * Function that returns a query for fetching a single row by ID.
 *
 * @typeParam TRow - The type of row data returned from queries
 */
export type GetSingleQuery<TRow> = (options: GetSingleQueryOptions) => QueryResult<TRow | undefined>

/**
 * Return type of a Zero query or query request.
 *
 * Uses `any` for schema/context to allow app-specific Zero schemas.
 *
 * @typeParam TReturn - The type of the query return value
 */
// oxlint-disable-next-line no-explicit-any
export type GetQueryReturnType<TReturn> = QueryOrQueryRequest<any, any, any, any, TReturn, any>

/**
 * Internal hook that manages the fetching and caching of rows for the virtualizer.
 *
 * @typeParam TRow - The type of row data returned from queries
 * @typeParam TStartRow - The type of data needed to anchor pagination
 */
export function useRows<TRow, TStartRow>({
  pageSize,
  anchor,
  settled,
  getPageQuery,
  getSingleQuery,
  toStartRow,
}: {
  pageSize: number
  anchor: Anchor<TStartRow>
  settled: boolean

  getPageQuery: GetPageQuery<TRow, TStartRow>
  getSingleQuery: GetSingleQuery<TRow>
  toStartRow: (row: TRow) => TStartRow
}): {
  rowAt: (index: number) => TRow | undefined
  rowsLength: number
  complete: boolean
  rowsEmpty: boolean
  atStart: boolean
  atEnd: boolean
  firstRowIndex: number
  permalinkNotFound: boolean
} {
  const {kind, index: anchorIndex} = anchor
  const isPermalink = kind === 'permalink'
  assert(!isPermalink || pageSize % 2 === 0)
  const halfPageSize = pageSize / 2

  // --- All hooks called unconditionally, in the same order on every render ---

  // Hook 1: single-item lookup (permalink only; null otherwise keeps hook count stable)
  const permalinkId = isPermalink ? (anchor as Extract<Anchor<TStartRow>, {kind: 'permalink'}>).id : ''
  const singleResult_ = isPermalink ? getSingleQuery({id: permalinkId, settled}) : null
  const [singleRow, singleResult] = useQuery(singleResult_?.query ?? null, singleResult_?.options)
  const typedSingleRow = singleRow as TRow | undefined
  const completeRow = singleResult.type === 'complete'
  const permalinkNotFound = isPermalink && completeRow && typedSingleRow === undefined

  const singleStart = typedSingleRow ? toStartRow(typedSingleRow) : null
  const pageStart = !isPermalink
    ? ((anchor as Extract<Anchor<TStartRow>, {kind: 'forward' | 'backward'}>).startRow ?? null)
    : null

  // Hook 2: page-before rows (permalink) OR main page rows (forward/backward)
  const q2Result = isPermalink
    ? !permalinkNotFound && singleStart
      ? getPageQuery({
          limit: halfPageSize + 1,
          start: singleStart,
          dir: 'backward',
          settled,
        })
      : null
    : getPageQuery({
        limit: pageSize + 1,
        start: pageStart,
        dir: kind as 'forward' | 'backward',
        settled,
      })
  const [rows2, result2] = useQuery(q2Result?.query ?? null, q2Result?.options)

  // Hook 3: page-after rows (permalink only; null for forward/backward)
  const q3Result =
    isPermalink && !permalinkNotFound && singleStart
      ? getPageQuery({
          limit: halfPageSize,
          start: singleStart,
          dir: 'forward',
          settled,
        })
      : null
  const [rows3, result3] = useQuery(q3Result?.query ?? null, q3Result?.options)

  // Derive values needed in useCallback before calling it
  const typedRows2 = rows2 as unknown as TRow[] | undefined
  const typedRows3 = rows3 as unknown as TRow[] | undefined

  const rowsBeforeLength = typedRows2?.length ?? 0
  const rowsAfterLength = typedRows3?.length ?? 0
  const rowsBeforeSize = Math.min(rowsBeforeLength, halfPageSize)
  const rowsAfterSize = Math.min(rowsAfterLength, halfPageSize - 1)

  const typedPageRows = (typedRows2 ?? []) as TRow[]
  const hasMoreRows = !isPermalink && typedPageRows.length > pageSize
  const paginatedRowsLength = hasMoreRows ? pageSize : typedPageRows.length

  // Hook 4: single unified rowAt — same hook, same dep-array size, every render
  const rowAt = useCallback(
    (index: number): TRow | undefined => {
      switch (kind) {
        case 'permalink': {
          if (index === anchorIndex) {
            return typedSingleRow
          }
          if (index > anchorIndex) {
            if (typedRows3 === undefined) return undefined
            const i = index - anchorIndex - 1
            return i < rowsAfterSize ? typedRows3[i] : undefined
          }
          if (typedRows2 === undefined) return undefined
          const i = anchorIndex - index - 1
          return i < rowsBeforeSize ? typedRows2[i] : undefined
        }
        case 'forward': {
          const i = index - anchorIndex
          return i >= 0 && i < paginatedRowsLength ? typedPageRows[i] : undefined
        }
        case 'backward': {
          const i = anchorIndex - index - 1
          return i >= 0 && i < paginatedRowsLength ? typedPageRows[i] : undefined
        }
        default:
          unreachable(kind)
      }
    },
    [
      isPermalink,
      kind,
      anchorIndex,
      typedSingleRow,
      typedRows2,
      typedRows3,
      rowsBeforeSize,
      rowsAfterSize,
      typedPageRows,
      paginatedRowsLength,
    ]
  )

  // --- Pure value branching (no hooks below this line) ---

  const complete2 = result2.type === 'complete'
  const complete3 = result3.type === 'complete'

  if (isPermalink) {
    return {
      rowAt,
      rowsLength: permalinkNotFound ? 0 : rowsBeforeSize + rowsAfterSize + (typedSingleRow ? 1 : 0),
      complete: completeRow && (permalinkNotFound || (complete2 && complete3)),
      rowsEmpty: permalinkNotFound || typedSingleRow === undefined || (rowsBeforeSize === 0 && rowsAfterSize === 0),
      atStart: permalinkNotFound || (complete2 && rowsBeforeLength <= halfPageSize),
      atEnd: permalinkNotFound || (complete3 && rowsAfterLength <= halfPageSize - 1),
      firstRowIndex: permalinkNotFound ? anchorIndex : anchorIndex - rowsBeforeSize,
      permalinkNotFound,
    }
  }

  kind satisfies 'forward' | 'backward'

  if (kind === 'forward') {
    return {
      rowAt,
      rowsLength: paginatedRowsLength,
      complete: complete2,
      rowsEmpty: typedPageRows.length === 0,
      atStart: pageStart === null || anchorIndex === 0,
      atEnd: complete2 && !hasMoreRows,
      firstRowIndex: anchorIndex,
      permalinkNotFound,
    }
  }

  kind satisfies 'backward'
  assert(pageStart !== null)

  return {
    rowAt,
    rowsLength: paginatedRowsLength,
    complete: complete2,
    rowsEmpty: typedPageRows.length === 0,
    atStart: complete2 && !hasMoreRows,
    atEnd: false,
    firstRowIndex: anchorIndex - paginatedRowsLength,
    permalinkNotFound,
  }
}
